import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { api } from "../api";
import { useAuth } from "../auth";
import { useI18n } from "../i18n";
import { ErrorNote, errorText } from "../ui";
import { AuthLayout } from "./AuthLayout";
import { PasswordInput } from "./PasswordInput";

const CHANNEL = "spos-auth";
const STORAGE_KEY = "spos.emailVerified";
const COOLDOWN_S = 60;

/**
 * (2026-10-10) Full-screen "Verify your email" gate (web + app): the shop owner can't use Senvia POS until the sign-up
 * address is confirmed. Shows the address, resend (60 s cooldown; server rate-limited), change a mistyped address,
 * sign out. Polls /auth/verification-status every 5 s (and on focus / when another tab confirms) and continues by itself
 * — also when the link was opened on another device. Team logins of such a shop see a short "not ready yet" screen.
 */
export function EmailGate() {
  const { me, refresh, logout } = useAuth();
  const { t } = useI18n();
  const gate = me?.emailGate;
  const owner = gate?.reason === "owner";
  const [email, setEmail] = useState(gate?.email ?? me?.user.email ?? "");
  const [cool, setCool] = useState(0);
  const [msg, setMsg] = useState<{ kind: "ok" | "warn"; text: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [devUrl, setDevUrl] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [pw, setPw] = useState("");
  const live = useRef(true);
  useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);

  useEffect(() => {
    if (cool <= 0) return;
    const h = setTimeout(() => setCool((c) => c - 1), 1000);
    return () => clearTimeout(h);
  }, [cool]);

  const check = useCallback(async (manual: boolean) => {
    try {
      if (owner) {
        const s = await api<{ verified: boolean; email: string; devLinkAvailable?: boolean }>("/api/auth/verification-status");
        if (!live.current) return;
        if (s.email) setEmail(s.email);
        if (s.verified) { await refresh(); return; }
        if (manual) {
          setMsg({ kind: "warn", text: t("iv.gate.notYet") });
          if (s.devLinkAvailable && !devUrl) {
            const d = await api<{ devUrl?: string }>("/api/auth/dev-verification-link", { method: "POST", body: {} });
            if (live.current && d.devUrl) setDevUrl(d.devUrl);
          }
        }
      } else {
        await refresh();
      }
    } catch (e) { if (manual && live.current) setErr(errorText(e)); }
  }, [owner, refresh, t, devUrl]);

  useEffect(() => {
    const tick = () => { if (typeof document === "undefined" || !document.hidden) void check(false); };
    const h = setInterval(tick, owner ? 5000 : 15000);
    const onStorage = (e: StorageEvent) => { if (e.key === STORAGE_KEY) void check(false); };
    let ch: BroadcastChannel | null = null;
    try { ch = new BroadcastChannel(CHANNEL); ch.onmessage = () => void check(false); } catch { /* old browser */ }
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", tick);
    document.addEventListener("visibilitychange", tick);
    return () => { clearInterval(h); ch?.close(); window.removeEventListener("storage", onStorage); window.removeEventListener("focus", tick); document.removeEventListener("visibilitychange", tick); };
  }, [check, owner]);

  const resend = async () => {
    setErr(null); setMsg(null); setBusy(true);
    try {
      const r = await api<{ sent?: boolean; alreadyVerified?: boolean; devUrl?: string }>("/api/auth/resend-verification", { method: "POST", body: {} });
      if (r.alreadyVerified) { await refresh(); return; }
      if (r.devUrl) setDevUrl(r.devUrl);
      setMsg({ kind: "ok", text: t("iv.gate.resent") }); setCool(COOLDOWN_S);
    } catch (e) { setErr(errorText(e)); } finally { if (live.current) setBusy(false); }
  };

  const saveEmail = async (e: FormEvent) => {
    e.preventDefault(); setErr(null); setMsg(null); setBusy(true);
    try {
      const r = await api<{ sent: boolean; email: string; devUrl?: string }>("/api/auth/change-email", { method: "POST", body: { email: newEmail.trim(), password: pw } });
      setEmail(r.email); setEditing(false); setPw(""); setNewEmail("");
      if (r.devUrl) setDevUrl(r.devUrl);
      if (r.sent) { setMsg({ kind: "ok", text: t("iv.gate.resent") }); setCool(COOLDOWN_S); }
      void refresh().catch(() => undefined);
    } catch (e2) { setErr(errorText(e2)); } finally { if (live.current) setBusy(false); }
  };

  return (
    <AuthLayout>
      <div className="auth-card email-gate" data-testid="email-gate" aria-live="polite">
        <div className="email-gate-icon" aria-hidden>
          <svg viewBox="0 0 24 24" width="34" height="34"><path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" d="M3 6.5h18v11H3zM3.5 7l8.5 6.5L20.5 7" /></svg>
        </div>
        {owner ? <>
          <h1>{t("iv.gate.title")}</h1>
          <p className="muted">{t("iv.gate.body")}</p>
          <p className="email-gate-address" data-testid="email-gate-address">{email}</p>
          <p className="small muted">{t("iv.gate.steps")}</p>
          {msg && <div className={"note " + (msg.kind === "ok" ? "note-info" : "note-warn")} role="status">{msg.text}</div>}
          <ErrorNote text={err} />
          {!editing ? <div className="stack">
            <button className="btn btn-primary btn-lg btn-block" disabled={busy} onClick={() => void check(true)}>{t("iv.gate.check")}</button>
            <button className="btn btn-soft btn-block" disabled={busy || cool > 0} onClick={() => void resend()} data-testid="email-gate-resend">{cool > 0 ? t("iv.gate.wait", { s: String(cool) }) : t("iv.gate.resend")}</button>
            <p className="small muted">{t("iv.gate.spam")}</p>
            {devUrl && <p className="small"><a href={devUrl} target="_blank" rel="noreferrer" data-testid="verify-dev-link">{t("check.devLink")}</a></p>}
            <div className="email-gate-links">
              <button type="button" className="btn btn-quiet btn-sm" onClick={() => { setEditing(true); setErr(null); setMsg(null); }}>{t("iv.gate.wrong")}</button>
              <button type="button" className="btn btn-quiet btn-sm" onClick={() => void logout()}>{t("iv.gate.signOut")}</button>
            </div>
          </div> : <form className="stack" onSubmit={saveEmail}>
            <label className="field"><span className="label">{t("iv.gate.newEmail")}</span>
              <input type="email" autoComplete="email" required value={newEmail} onChange={(e) => setNewEmail(e.target.value)} autoFocus /></label>
            <label className="field"><span className="label">{t("iv.gate.password")}</span>
              <PasswordInput autoComplete="current-password" required value={pw} onChange={setPw} /></label>
            <button className="btn btn-primary btn-block" disabled={busy || !newEmail.includes("@") || !pw}>{t("iv.gate.saveEmail")}</button>
            <button type="button" className="btn btn-quiet btn-block" onClick={() => setEditing(false)}>{t("iv.gate.cancel")}</button>
          </form>}
        </> : <>
          <h1>{t("iv.gate.shopTitle")}</h1>
          <p className="muted">{t("iv.gate.shopBody")}</p>
          <ErrorNote text={err} />
          <div className="stack">
            <button className="btn btn-primary btn-block" onClick={() => void check(true)}>{t("iv.gate.retry")}</button>
            <button className="btn btn-quiet btn-block" onClick={() => void logout()}>{t("iv.gate.signOut")}</button>
          </div>
        </>}
      </div>
    </AuthLayout>
  );
}
